import img_close_cross from "../../../img/closeCrossIcon.svg";
import { FormattedMessage, useIntl } from "react-intl";
import React, { useRef, useState, useEffect } from "react";
import { 
  Button, 
  Divider, 
  Box, 
  Paper, 
  Typography, 
  Stack, 
  IconButton,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Avatar,
  Chip
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { ChooseLanguageDropdown } from "../../layout/header/choose-language-dropdown";
import { RegisterFields } from "./register-fields";
import { useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import {
  checkRequiredSocialInputsFilled,
  handleRegister,
} from "./handleRegister";
import { REGISTER } from "../../../apollo-client/mutations";
import { RegisterSocialButtons } from "./register-social-buttons";
import { useSidebarStore } from "../../../store/sidebarStore";

export const RegisterSidebar = ({ isOpen, setIsOpen }) => {
  const [formValues, setFormValues] = useState({
    email: "",
    name: "",
    university: "",
    degreeProgram: "",
    password: "",
  });
  const [formValueErrors, setFormValueErrors] = useState({
    email: false,
    password: false,
    name: false,
    university: false,
    degreeProgram: false,
    areAGB: false,
  });
  const { ssoRegisterSidebar: ssoState, setSsoRegisterSidebar: setSsoState } = useSidebarStore();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const location = useLocation();

  const intl = useIntl();
  const history = useHistory();

  // Close sidebar when navigating to a different page
  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  }, [location.pathname, setIsOpen]);

  const emailRef = useRef(null);
  const nameRef = useRef(null);
  const universityRef = useRef(null);
  const degreeProgramRef = useRef(null);
  const passwordRef = useRef(null);

  const [registerMutation] = useMutation(REGISTER);

  const register = async () => {
    await handleRegister({
      registrationFormValues: formValues,
      registrationFormValueErrors: formValueErrors,
      setRegistrationFormValueErrors: setFormValueErrors,
      intl,
      closeRegistrationSidebar: closeSidebar,
      register: registerMutation,
      refs: {
        emailRef,
        nameRef,
        passwordRef,
        universityRef,
        degreeProgramRef,
      },
    });
    // go back to landing page
  };

  const checkFormFilledForSocial = () =>
    checkRequiredSocialInputsFilled({
      registrationFormValues: formValues,
      registrationFormValueErrors: formValueErrors,
      setRegistrationFormValueErrors: setFormValueErrors,
      refs: {
        emailRef,
        nameRef,
        passwordRef,
        universityRef,
        degreeProgramRef,
      },
      intl,
    });

  const closeSidebar = () => {
    history.push("/");
    setIsOpen(false);
  };

  const openSSOSidebar = (provider) => {
    setSsoState({ isOpen: true, provider });
    setIsOpen(false); // Close the main register sidebar
  };

  const closeSSOSidebar = () => {
    setSsoState({ isOpen: false, provider: "" });
    setIsOpen(true); // Reopen the main register sidebar
  };

  return (
    <Slide direction="left" in={isOpen} timeout={400}>
      <Paper
        elevation={0}
        sx={{
          position: 'fixed',
          height: '100vh',
          height: '100dvh', // Dynamic viewport height for mobile
          width: isDesktop ? '420px' : '100%',
          maxWidth: isDesktop ? '420px' : '100%',
          top: 0,
          right: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderLeft: '1px solid rgba(255, 255, 255, 0.2)',
          zIndex: 13,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          // Mobile-specific improvements
          '@media (max-width: 768px)': {
            height: '100vh',
            height: '100dvh',
            paddingBottom: 'env(safe-area-inset-bottom)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #E87975 0%, #F86B6B 50%, #FF8181 100%)',
            zIndex: 1,
          }
        }}
        data-testid="register-sidebar"
      >
        {/* Header */}
        <Fade in={isOpen} timeout={600}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '24px 32px',
              background: 'linear-gradient(135deg, rgba(232, 121, 117, 0.05) 0%, rgba(248, 107, 107, 0.05) 100%)',
              borderBottom: '1px solid rgba(232, 121, 117, 0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  background: 'linear-gradient(135deg, #E87975 0%, #F86B6B 100%)',
                  fontSize: '18px',
                  fontWeight: 'bold',
                }}
              >
                B
              </Avatar>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: '20px',
                    background: 'linear-gradient(135deg, #E87975 0%, #F86B6B 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    lineHeight: 1.2,
                  }}
                  data-testid="register-sidebar-title"
                >
                  <FormattedMessage id="general.register-button" />
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.grey[600],
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                >
                  Create your account to get started
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={closeSidebar}
              data-testid="register-sidebar-close-button"
              sx={{ 
                padding: '12px',
                backgroundColor: 'rgba(232, 121, 117, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(232, 121, 117, 0.2)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <img 
                src={img_close_cross} 
                alt="Close"
                style={{ width: '20px', height: '20px' }}
              />
            </IconButton>
          </Box>
        </Fade>

        {/* Main Content - Scrollable */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(232, 121, 117, 0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(232, 121, 117, 0.3)',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(232, 121, 117, 0.5)',
          },
        }}>
          <Fade in={isOpen} timeout={800}>
            <Box sx={{ 
              padding: isDesktop ? '32px' : '24px', 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 3,
              minHeight: '100%',
            }}>
              <RegisterFields
                formValues={formValues}
                formValueErrors={formValueErrors}
                setFormValueErrors={setFormValueErrors}
                setFormValues={setFormValues}
                refs={{
                  emailRef,
                  nameRef,
                  passwordRef,
                  universityRef,
                  degreeProgramRef,
                }}
                register={register}
              />

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Button
                  onClick={register}
                  variant="contained"
                  sx={{
                    width: '100%',
                    maxWidth: '320px',
                    height: '52px',
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 700,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #E87975 0%, #F86B6B 100%)',
                    boxShadow: '0 4px 15px rgba(232, 121, 117, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #F86B6B 0%, #FF8181 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(232, 121, 117, 0.4)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <FormattedMessage id="general.register-button" />
                </Button>
              </Box>

              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', my: 2 }}>
                <Divider sx={{ flex: 1, borderColor: 'rgba(232, 121, 117, 0.2)' }} />
                <Chip
                  label={<FormattedMessage id="sign-up.or" />}
                  size="small"
                  sx={{
                    mx: 2,
                    backgroundColor: 'rgba(232, 121, 117, 0.1)',
                    color: theme.palette.grey[600],
                    fontWeight: 600,
                    fontSize: '12px',
                    border: '1px solid rgba(232, 121, 117, 0.2)',
                  }}
                />
                <Divider sx={{ flex: 1, borderColor: 'rgba(232, 121, 117, 0.2)' }} />
              </Box>

              <RegisterSocialButtons
                onOpenSSOSidebar={openSSOSidebar}
              />

              {/* Footer - Scrollable */}
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ borderColor: 'rgba(232, 121, 117, 0.1)', mb: 3 }} />
                <Stack
                  direction="column"
                  alignItems="center"
                  spacing={2}
                  sx={{ 
                    padding: isDesktop ? '0 0 24px 0' : '0 0 20px 0',
                    background: 'linear-gradient(135deg, rgba(232, 121, 117, 0.02) 0%, rgba(248, 107, 107, 0.02) 100%)',
                    borderRadius: '12px',
                    padding: '20px',
                  }}
                >
                  <ChooseLanguageDropdown />
                  <Button
                    component={Link}
                    to="/help"
                    variant="text"
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      color: theme.palette.grey[600],
                      textTransform: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: 'rgba(232, 121, 117, 0.1)',
                        color: '#E87975',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <FormattedMessage id="menu.info-links.help" />
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Fade>
        </Box>
      </Paper>
    </Slide>
  );
};

